import { createPinia, defineStore } from "pinia";

import { useInventoryStore } from "../stores/inventory";
import { mergeLiveInventoryWithPermissions } from "../utils/inventoryVisibility";

export const pinia = createPinia();

let vuehost = import.meta.env.VITE_APP_HOST;

const AUTH_TOKEN_KEYS = ["subangstrom_auth_jwt", "jwt"];

function getStoredAuthToken() {
  if (typeof window === "undefined") return null;
  return AUTH_TOKEN_KEYS.map((key) => window.localStorage.getItem(key)).find(Boolean) || null;
}

function setStoredAuthToken(token) {
  if (typeof window === "undefined" || !token) return;
  window.localStorage.setItem(AUTH_TOKEN_KEYS[0], token);
}

function clearStoredAuthToken() {
  if (typeof window === "undefined") return;
  AUTH_TOKEN_KEYS.forEach((key) => window.localStorage.removeItem(key));
}

function authHeaders(baseHeaders = {}) {
  const token = getStoredAuthToken();
  return token
    ? {
        ...baseHeaders,
        Authorization: `Bearer ${token}`,
      }
    : baseHeaders;
}

function dtoEntityId(value) {
  if (!value) return null;
  if (typeof value === "string") return value;
  return value.id || null;
}

function normalizeSqlDto(value) {
  if (Array.isArray(value)) {
    return value.map(normalizeSqlDto);
  }

  if (!value || typeof value !== "object") {
    return value;
  }

  const normalized = {};
  Object.keys(value).forEach((key) => {
    normalized[key] = normalizeSqlDto(value[key]);
  });

  if (normalized.id && !normalized.id) {
    normalized.id = normalized.id || normalized.id;
  }

  return normalized;
}

const INVENTORY_ITEMS = new Set(["dewars", "racks", "pucks", "boxes"]);

function getInventoryStore() {
  return useInventoryStore(pinia);
}

function isInventoryItem(item) {
  return INVENTORY_ITEMS.has(item);
}

function syncLiveDewarData(state, serverTree = state.dewarData) {
  try {
    const inventoryStore = getInventoryStore();
    const liveTree = inventoryStore.buildLegacyTree(state.activeUser?.labId);
    if (!liveTree.length) {
      return serverTree;
    }
    const hasServerTree = Array.isArray(serverTree) && serverTree.length > 0;
    if (!hasServerTree && state.activeUser?.role !== "lab-admin") {
      return serverTree;
    }
    return mergeLiveInventoryWithPermissions(
      liveTree,
      state.inventoryPermissions,
      serverTree
    );
  } catch {
    return serverTree;
  }
}

if (window.location.hostname == "devtest.subangstrom.com") {
  vuehost = "https://nodedev.subangstrom.com";
}

export const useAppStore = defineStore("app", {
  state: () => ({
    importModalMounted: false,
    gridChangedPuckVisualTrigger: false,
    gridSaveInProgress: false,
    gridSaveTimeout: null,
    gridSaveLastWellData: null,
    unsavedChangesMPGrid: false,
    unsavedChangesMPBox: false,
    noAdminUsers: [],
    usersOnline: new Set(),
    sensors: [],
    promptAnswerResolve: null,
    promptAnswerReject: null,
    promptUnsaveModal: null,
    userDataResolve: null,
    userDataReject: null,
    boxTemplates: [],
    checkFor: null,
    moveTo: null,
    moveToForFollow: null,
    serverdate: null,
    userDataAlreadyLoaded: false,
    isLoggedIn: false,
    dewarData: [],
    inventoryPermissions: null,
    isMark: false,
    isLabSuper: false,
    hostname: vuehost,
    unassignedPucks: [],
    unassignedRacks: [],
    institutes: [],
    disableBoxUpdate: false,
    users: [],
    logs: [],
    externalusers: [],
    labs: [],
    transports: [],
    projects: [],
    refreshtransport: false,
    transportrefresh: false,
    transportUpdate: false,
    activeUser: [],
    puckColors: [
      "red",
      "orange",
      "green",
      "blue",
      "violet",
      "black",
      "chrome",
      "pink",
      "sky blue",
      "gold",
      "honey",
    ],
    boxColors: [
      "gold",
      "white",
      "sky blue",
      "red",
      "violet",
      "pink",
      "orange",
      "green",
      "chrome",
      "brown",
      "blue",
      "black",
    ],
  }),

  getters: {
    isAuthenticated: (state) => state.isLoggedIn,

    nonTempUsers(state) {
      return state.users.filter((user) => user.role != "temp");
    },

    nonTempUsersNoAdmin(state) {
      return state.users.filter(
        (user) => user.role != "temp" && user.role != "lab-admin"
      );
    },

  },

  actions: {
    promptAnswerPromise() {
      return new Promise((res, rej) => {
        this.promptAnswerResolve = res;
        this.promptAnswerReject = rej;
      });
    },

    getUsersForLab() {
      if (this.activeUser?.role !== "lab-admin") {
        return Promise.resolve([]);
      }
      const labid = this.activeUser.labId?.id;
      return this.dispatch("getAdminStuff", {
        url: "users",
        labid,
      });
    },

    transformPuckColor(color, isSvgCup) {
      let hex = "#";
      switch (color) {
        case "red":
          hex += "FF0000";
          break;
        case "blue":
          hex += "0000FF";
          break;
        case "green":
          hex += "008000";
          break;
        case "orange":
          hex += "FA6800";
          break;
        case "violet":
          hex += "EE82EE";
          break;
        case "chrome":
          hex += "dbe4eb";
          break;
        case "gold":
          hex += "FFD700";
          break;
        case "honey":
          hex += "EBA937";
          break;
        case "sky blue":
          hex += "87ceeb";
          break;
        case "pink":
          hex += "FFC0CB";
          break;
        default:
          hex += "000000";
      }
      if (isSvgCup) {
        return this.hexToDarkerRgb(hex);
      }
      return hex;
    },

    twoDigitsPlease(digit) {
      return parseInt(digit).toLocaleString("en-US", {
        minimumIntegerDigits: 2,
        useGrouping: false,
      });
    },

    transformBoxColor(color, isSvgCup) {
      let hex = "#";
      switch (color) {
        case "red":
          hex += "FF0000";
          break;
        case "blue":
          hex += "0000FF";
          break;
        case "green":
          hex += "008000";
          break;
        case "orange":
          hex += "FFA500";
          break;
        case "violet":
          hex += "EE82EE";
          break;
        case "chrome":
          hex += "dbe4eb";
          break;
        case "gold":
          hex += "FFD700";
          break;
        case "sky blue":
          hex += "87ceeb";
          break;
        case "pink":
          hex += "FFC0CB";
          break;
        case "black":
          hex += "000000";
          break;
        case "white":
          hex += "FFFFFF";
          break;
        case "brown":
          hex += "8F3F25";
          break;
        default:
          hex += "000000";
      }
      if (isSvgCup) {
        return this.hexToDarkerRgb(hex);
      }
      return hex;
    },

    buttonFXinit(buttonev) {
      const button = buttonev.target.closest("button");
      button.children[0].style.display = "flex";
      button.children[1].style.visibility = "hidden";
      button.disabled = true;
    },

    buttonFX(button, message, redirect) {
      button.children[0].style.display = "none";
      button.children[1].style.display = "flex";
      button.children[1].style.justifyContent = "center";
      button.children[1].style.visibility = "visible";

      if (message) {
        button.nextSibling.textContent = message;
        button.nextSibling.addEventListener("transitionend", () => {
          button.nextSibling.classList.remove("fadeinbutton");
          button.nextSibling.classList.add("fadeoutbutton");
          if (redirect) window.location.replace("/");
        });

        button.nextSibling.classList.remove("fadeoutbutton");
        button.nextSibling.classList.add("fadeinbutton");
      }
      button.disabled = false;
    },

    hexToDarkerRgb(hex) {
      var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      if (hex == "#000000") {
        return result
          ? `rgb(
              ${parseInt(result[1], 16) + 80},
              ${parseInt(result[2], 16) + 80},
              ${parseInt(result[3], 16) + 80}
            )`
          : null;
      }
      return result
        ? `rgb(
              ${parseInt(result[1], 16) - 80},
              ${parseInt(result[2], 16) - 80},
              ${parseInt(result[3], 16) - 80}
            )`
        : null;
    },

    cleanDate(date, shortmonth, notime) {
      let month = "long";
      if (shortmonth) month = "short";
      var options = {
        year: "numeric",
        month: month,
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
      };
      if (notime) {
        delete options.hour;
        delete options.minute;
      }

      var today = new Date(date);

      return today.toLocaleString("en-US", options) == "Invalid Date"
        ? ""
        : today.toLocaleString("en-US", options);
    },

    manageUsersCustomAccessFnc(itemtype, itemid, customaccess, user, isexternal) {
      return new Promise((res, rej) => {
        const body = {
          customaccess,
          objtype: itemtype.charAt(0).toUpperCase() + itemtype.slice(1),
          [itemtype + "id"]: itemid,
        };
        if (isexternal) {
          body.findbyemail = true;
          body.sendingId = true;
        }

        this.dispatch("updateItem", {
            objid: user,
            item: "users",
            body,
          })
          .then(() => res())
          .catch(() => rej());
      });
    },
  },
});

export const appStore = useAppStore(pinia);

const mutationHandlers = {
  updateUserLabData(state, labData) {
    if (state.activeUser && state.activeUser.labId) {
      state.activeUser.labId = labData;
    }
  },

  ADD_SENSOR(state, sensor) {
    sensor = normalizeSqlDto(sensor);
    const existingIndex = state.sensors.findIndex((entry) => entry.id === sensor.id);

    if (existingIndex !== -1) {
      state.sensors[existingIndex] = sensor;
    } else {
      state.sensors.push(sensor);
    }
  },

  login(state) {
    state.isLoggedIn = true;
  },

  logout(state) {
    clearStoredAuthToken();
    state.isLoggedIn = false;
    state.activeUser = [];
    state.userDataAlreadyLoaded = false;
    state.isMark = false;
    state.isLabSuper = false;
  },

  updateDewarData(state, payload) {
    if (payload.permissions !== undefined) {
      state.inventoryPermissions = payload.permissions;
    }
    state.dewarData = syncLiveDewarData(state, payload.value);
  },

  updateUnassignedPucks(state, payload) {
    if (!payload.isRack) state.unassignedPucks = payload.value;
    else state.unassignedRacks = payload.value;
  },

  updateAdminStuff(state, payload) {
    const nextValue = normalizeSqlDto(payload.value);
    if (payload.stateName == "logs" && payload.logpagemore) {
      state[payload.stateName] = [
        ...state[payload.stateName],
        ...nextValue,
      ];
    } else {
      state[payload.stateName] = nextValue;
    }
  },

  updateUserData(state, payload) {
    state.activeUser = normalizeSqlDto(payload.value);
    if (state.activeUser?.role === "lab-admin") {
      state.getUsersForLab();
    }
    if (payload.value.isMark) {
      state.isMark = true;
    }

    if (payload.value.isLabSuper) {
      state.isLabSuper = true;
    }

    state.userDataAlreadyLoaded = true;
    if (state.checkFor == "mark") {
      if (state.isMark) state.userDataResolve?.();
      else state.userDataReject?.();
    } else if (state.checkFor == "lab-admin") {
      if (state.activeUser.role == "lab-admin") state.userDataResolve?.();
      else state.userDataReject?.();
    } else if (state.checkFor == "lab-super") {
      if (state.isLabSuper) state.userDataResolve?.();
      else state.userDataReject?.();
    }
  },

  setAuthToken(state, token) {
    setStoredAuthToken(token);
  },
};

const actionHandlers = {
  addSensor({ commit }, sensor) {
    commit("ADD_SENSOR", sensor);
  },

  userDataGot(context, payload) {
    return new Promise((resolve, reject) => {
      if (payload?.labadmincheck) context.state.checkFor = "lab-admin";
      else if (payload?.labsuper) context.state.checkFor = "lab-super";
      else context.state.checkFor = "mark";
      context.state.userDataResolve = resolve;
      context.state.userDataReject = reject;
    });
  },

  adminCreateRackAndPucks(context, payload) {
    return new Promise((resolve, reject) => {
      const url = `${context.state.hostname}/api/v1/racks/createloadedrack`;
      const request = new Request(url, {
        credentials: "include",
        method: "POST",
        body: JSON.stringify(payload.body),
        headers: { "content-type": "application/json" },
      });
      fetch(request)
        .then((resp) => resp.json())
        .then((data) => {
          if (data.status == "fail" || data.status == "error") {
            throw new Error(data.message);
          }
          resolve();
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  pushPuckToRack(context, payload) {
    return new Promise((res, rej) => {
      const inventoryStore = getInventoryStore();
      Promise.resolve(inventoryStore.assignPuckToRack(payload.puckid, payload.rackid))
        .then(() => {
          context.commit("updateDewarData", { value: context.state.dewarData });
          if (payload.fromtransport) {
            context.state.transportUpdate = true;
          }
          if (!payload.fromtransport) {
            const nextLab = payload.lab || context.state.activeUser?.labId?.id;
            context.dispatch("getUnassignedPucks", { lab: nextLab });
          }
          res();
        })
        .catch((err) => {
          rej(err);
        });
    });
  },

  pushRackToDewar(context, payload) {
    const inventoryStore = getInventoryStore();
    return Promise.resolve(
      inventoryStore.assignRackToDewar(
        payload.rackid,
        payload.removeRack ? null : payload.dewarid
      )
    ).then(() => {
      context.commit("updateDewarData", { value: context.state.dewarData });
    });
  },

  getAdminStuff(context, payload) {
    return new Promise((res, rej) => {
      let url = context.state.hostname + "/api/v1/" + payload.url;
      if (payload.url == "users" || payload.url == "logs") {
        url =
          context.state.hostname +
          "/api/v1/" +
          payload.url +
          "?labid=" +
          payload.labid;
      }

      if (payload.url == "externalusers") {
        context.state.externalusers = [];
        url =
          context.state.hostname +
          "/api/v1/users/" +
          payload.url +
          "?labid=" +
          payload.labid;
      }

      if (payload.page) url += "&page=" + payload.page;

      if (payload.projusers) {
        url =
          context.state.hostname +
          "/api/v1/projects/" +
          payload.projusers +
          "/linkuser";
      }

      const request = new Request(url, {
        credentials: "include",
        method: "GET",
        headers: { "content-type": "application/json" },
      });
      fetch(request)
        .then((resp) => resp.json())
        .then((data) => {
          if (data.status == "fail") throw new Error(data.message);

          res(data.data.data);

          if (payload.url === "labs/boxtemplates") {
            context.commit("updateUserLabData", data.data.data);
          } else {
            context.commit("updateAdminStuff", {
              value: data.data.data,
              stateName: payload.url,
              signal: payload.signal,
              logpagemore: payload.page,
            });
          }
        })
        .catch((err) => {
          rej(err);
        });
    });
  },

  getUnassignedPucks(context, payload) {
    const inventoryStore = getInventoryStore();
    if (payload.lab === context.state.activeUser?.labId?.id || !payload.lab) {
      const value = payload.isRack
        ? inventoryStore.getUnassignedRacks
        : inventoryStore.getUnassignedPucks;
      context.commit("updateUnassignedPucks", {
        value,
        ...(payload.isRack ? { isRack: true } : {}),
      });
      return;
    }

    let modelPath = "pucks";
    if (payload.isRack) modelPath = "racks";

    const url =
      context.state.hostname +
      "/api/v1/" +
      modelPath +
      "/labs/" +
      payload.lab;
    const request = new Request(url, {
      credentials: "include",
      method: "GET",
      headers: { "content-type": "application/json" },
    });
    fetch(request)
      .then((resp) => resp.json())
      .then((data) => {
        if (data.status == "fail") throw new Error(data.message);
        const payloadObj = {
          value: data.data.data,
        };
        if (payload.isRack) payloadObj.isRack = true;

        context.commit("updateUnassignedPucks", payloadObj);
      })
      .catch((err) => console.log(err));
  },

  getArchives(context, payload) {
    return new Promise((res, rej) => {
      const url =
        context.state.hostname +
        "/api/v1/gridarchives?type=" +
        payload.importType;
      const request = new Request(url, {
        credentials: "include",
        method: "GET",
        headers: { "content-type": "application/json" },
      });
      fetch(request)
        .then((resp) => resp.json())
        .then((data) => {
          if (data.status == "fail") throw new Error(data.message);
          return res(data.data.data);
        })
        .catch((err) => {
          console.log(err);
          return rej("error fetching archives");
        });
    });
  },

  updateItem(context, payload) {
    return new Promise((res, rej) => {
      const {
        item,
        objid,
        body,
        setAdmin,
        labcode,
        forpayload,
        linkuser,
        skipDewarReload,
        skipVisualRefresh,
      } = payload;

      if (forpayload) {
        if (item == "dewars") {
          if (
            context.state.dewarData[forpayload.labIndex].data[
              forpayload.dewarIndex
            ][forpayload.propertytoupdate] ==
            body[forpayload.propertytoupdate]
          )
            return res();
        } else if (item == "racks") {
          if (
            context.state.dewarData[forpayload.labIndex].data[
              forpayload.dewarIndex
            ].racks[forpayload.rackIndex][forpayload.propertytoupdate] ==
            body[forpayload.propertytoupdate]
          )
            return res();
        } else if (item == "pucks") {
          if (
            context.state.dewarData[forpayload.labIndex].data[
              forpayload.dewarIndex
            ].racks[forpayload.rackIndex].pucks[forpayload.puckIndex][
              forpayload.propertytoupdate
            ] == body[forpayload.propertytoupdate]
          )
            return res();
        } else if (
          item == "boxes" &&
          forpayload.propertytoupdate != "gridProperty"
        ) {
          if (
            context.state.dewarData[forpayload.labIndex].data[
              forpayload.dewarIndex
            ].racks[forpayload.rackIndex].pucks[forpayload.puckIndex][
              "well" + forpayload.wellNo
            ][forpayload.propertytoupdate] ==
            body[forpayload.propertytoupdate]
          )
            return res();
        }
      }

      if (isInventoryItem(item)) {
        try {
          const inventoryStore = getInventoryStore();
          let mutation = null;
          if (item === "dewars") {
            mutation = inventoryStore.updateDewar(objid, body);
          } else if (item === "racks") {
            mutation = inventoryStore.updateRack(objid, body);
          } else if (item === "pucks") {
            mutation = inventoryStore.updatePuck(objid, {
              ...body,
              rackId: body.rackId ?? body.rackid ?? body.rackID,
            });
          } else if (item === "boxes") {
            mutation = inventoryStore.updateBox(objid, body);
          }

          Promise.resolve(mutation)
            .then(() => {
              context.commit("updateDewarData", { value: context.state.dewarData });
              context.state.refreshtransport = true;
              if (body.moveTo && payload.samepuck) {
                context.state.moveTo = body.moveTo.toWell;
              }
              if (!skipVisualRefresh) {
                context.state.gridChangedPuckVisualTrigger = true;
              }
              res("ok");
            })
            .catch((err) => {
              rej(err);
            });
          return;
        } catch (err) {
          rej(err);
          return;
        }
      }

      let url = `${context.state.hostname}/api/v1/${item}/${objid}`;
      if (setAdmin) {
        url = `${context.state.hostname}/api/v1/${item}/${objid}?setAdmin=true`;
      }
      if (linkuser) {
        url = `${context.state.hostname}/api/v1/${item}/${objid}/linkuser`;
      }
      if (labcode) {
        url =
          `${context.state.hostname}/api/v1/${item}/${objid}?addLabCode=` +
          labcode;
      }

      if (item != "projects" && forpayload?.propertytoupdate != "project") {
        body.dateModified = Date.now();
      }

      const request = new Request(url, {
        credentials: "include",
        method: "PATCH",
        body: JSON.stringify(body),
        headers: { "content-type": "application/json" },
      });
      fetch(request)
        .then((resp) => resp.json())
        .then(async (data) => {
          if (data.status == "fail" || data.status == "error") {
            throw new Error(data.message);
          }

          if (item == "labs" && labcode) {
            console.log("maybne do something else");
          } else if (item == "labs") {
            await context.dispatch("getAdminStuff", { url: "labs" });
          } else if (item == "users") {
            await context.dispatch("getAdminStuff", {
              url: "users",
              labid: payload.labid ?? context.state.activeUser.labId?.id,
            });
          } else if (!payload.body.customaccess) {
            if (payload.linkuser && payload.body.delete) {
              res("deleted");
            } else if (!skipDewarReload) {
              await context.dispatch("updateDewarData");
            }
            context.state.refreshtransport = true;
          }

          if (body.moveTo && payload.samepuck) {
            context.state.moveTo = body.moveTo.toWell;
          }

          if (!skipVisualRefresh) {
            context.state.gridChangedPuckVisualTrigger = true;
          }
          res("ok");
        })
        .catch((err) => {
          rej(err);
        });
    });
  },

  deleteItem(context, payload) {
    return new Promise((res, rej) => {
      const { item, objid, body } = payload;

      if (isInventoryItem(item)) {
        try {
          const inventoryStore = getInventoryStore();
          let mutation = null;
          if (item === "dewars") mutation = inventoryStore.deleteDewar(objid);
          else if (item === "racks") mutation = inventoryStore.deleteRack(objid);
          else if (item === "pucks") mutation = inventoryStore.deletePuck(objid);
          else if (item === "boxes") mutation = inventoryStore.deleteBox(objid);
          Promise.resolve(mutation)
            .then(() => {
              context.commit("updateDewarData", { value: context.state.dewarData });
              res();
            })
            .catch((err) => {
              rej(err);
            });
          return;
        } catch (err) {
          rej(err);
          return;
        }
      }

      const url = `${context.state.hostname}/api/v1/${item}/${objid}`;
      const request = new Request(url, {
        credentials: "include",
        method: "DELETE",
        body: JSON.stringify(body),
        headers: { "content-type": "application/json" },
      });
      fetch(request)
        .then((resp) => {
          if (resp.status == 204 || resp.status == 200) {
            if (item == "users") {
              context.dispatch("getAdminStuff", {
                url: "users",
                labid: payload.labid ?? context.state.activeUser.labId?.id,
              });
            } else if (item != "gridarchives") {
              context.dispatch("updateDewarData");
            }

            if (item == "gridarchives") console.log("archive deleted");

            res();
          }
        })
        .catch(() => {
          rej();
        });
    });
  },

  login(context, payload = {}) {
    if (payload.token) {
      context.commit("setAuthToken", payload.token);
    }

    return context
      .dispatch("getUserData", { url: "users/me" })
      .then(() => {
        context.commit("login");
      })
      .catch((err) => {
        context.commit("logout");
        throw err;
      });
  },

  logout(context) {
    const url = context.state.hostname + "/api/v1/users/logout";
    const request = new Request(url, {
      credentials: "include",
      method: "GET",
      headers: authHeaders({ "content-type": "application/json" }),
    });
    fetch(request)
      .then((resp) => resp.json())
      .then((data) => {
        if (data.status == "fail") throw new Error(data.message);
        context.commit("logout");
      })
      .catch((err) => console.log(err));
  },

  updateDewarData(context) {
    return new Promise((res, rej) => {
      const url = context.state.hostname + "/api/v1/dewars";
      const permissionsUrl = context.state.hostname + "/api/v1/dewars/permissions";
      const request = new Request(url, {
        credentials: "include",
        method: "GET",
        headers: { "content-type": "application/json" },
      });
      const permissionsRequest = new Request(permissionsUrl, {
        credentials: "include",
        method: "GET",
        headers: { "content-type": "application/json" },
      });
      Promise.all([fetch(request), fetch(permissionsRequest)])
        .then(async ([resp, permissionsResp]) => {
          const [data, permissions] = await Promise.all([
            resp.json(),
            permissionsResp.json(),
          ]);
          if (data.status == "fail") throw new Error(data.message);
          if (permissions.status == "fail") throw new Error(permissions.message);
          context.commit("updateDewarData", {
            value: data.data.data,
            permissions: permissions.data.data,
          });
          return res();
        })
        .catch(() => {
          rej();
        });
    });
  },

  getUserData(context, payload) {
    const url = context.state.hostname + "/api/v1/" + payload.url;
    const request = new Request(url, {
      credentials: "include",
      method: "GET",
      headers: { "content-type": "application/json" },
    });
    return fetch(request)
      .then(async (resp) => {
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          throw new Error(data.message || "Session check failed");
        }
        return data;
      })
      .then((data) => {
        if (data.status == "fail") throw new Error(data.message);
        context.commit("updateUserData", { value: data.data.data });
        return data;
      })
      .catch((err) => {
        context.commit("logout");
        console.log(err);
        throw err;
      });
  },

  createItem(context, payload) {
    return new Promise((res, rej) => {
      const { item, body, dewarId, skipDewarReload, skipVisualRefresh } = payload;

      if (isInventoryItem(item)) {
        try {
          const inventoryStore = getInventoryStore();
          let createMutation = null;

          if (item === "dewars") createMutation = inventoryStore.createDewar(body);
          else if (item === "racks") {
            createMutation = inventoryStore.createRack({
              ...body,
              dewarId: body.dewarId || dewarId || null,
            });
          } else if (item === "pucks") createMutation = inventoryStore.createPuck(body);
          else if (item === "boxes") createMutation = inventoryStore.createBox(body);

          Promise.resolve(createMutation)
            .then((createdId) => {
              if (item === "boxes" && createdId) {
                res(inventoryStore.getBox(createdId));
              } else {
                res(item + " added");
              }

              context.commit("updateDewarData", { value: context.state.dewarData });
              context.state.refreshtransport = true;
              if (!skipVisualRefresh) {
                context.state.gridChangedPuckVisualTrigger = true;
              }
            })
            .catch((err) => {
              rej(err);
            });
          return;
        } catch (err) {
          rej(err);
          return;
        }
      }

      let url;
      if (dewarId) {
        url = `${context.state.hostname}/api/v1/dewars/${dewarId}/${item}`;
      } else {
        url = `${context.state.hostname}/api/v1/${item}`;
      }
      const request = new Request(url, {
        credentials: "include",
        method: "POST",
        body: JSON.stringify(body),
        headers: { "content-type": "application/json" },
      });
      fetch(request)
        .then((resp) => resp.json())
        .then((data) => {
          if (data.status == "fail") throw new Error(data.message);

          if (item === "boxes" && data?.data?.data) res(data.data.data);
          else res(item + " added");

          if (item == "institutes") {
            context.dispatch("getAdminStuff", { url: "institutes" });
          } else if (item == "labs") {
            context.dispatch("getAdminStuff", { url: "labs" });
          } else if (item == "users") {
            context.dispatch("getAdminStuff", { url: "users" });
          } else if (item == "projects") {
            context.dispatch("getAdminStuff", { url: "projects" });
          } else if (item == "gridarchives") {
            console.log("grid archive created");
          } else if (!skipDewarReload) {
            context.dispatch("updateDewarData");
            context.state.refreshtransport = true;
          }
          if (!skipVisualRefresh) {
            context.state.gridChangedPuckVisualTrigger = true;
          }
          if (item == "transports") {
            context.dispatch("getAdminStuff", { url: "transports" });
          }
          if (body.boxTemplateName) {
            context.dispatch("getAdminStuff", {
              url: "labs/boxtemplates",
            });
          }
        })
        .catch(() => {
          rej("error :(");
        });
    });
  },
};

const getterProxy = new Proxy(
  {},
  {
    get(_target, prop) {
      return appStore[prop];
    },
  }
);

function createActionContext(store = appStore) {
  return {
    state: store,
    commit: (type, payload) => mutationHandlers[type](store, payload),
    dispatch: (type, payload) => actionHandlers[type](createActionContext(store), payload),
  };
}
Object.defineProperties(appStore, {
  state: {
    get() {
      return appStore;
    },
  },
  getters: {
    get() {
      return getterProxy;
    },
  },
  commit: {
    value(type, payload) {
      const mutation = mutationHandlers[type];
      if (!mutation) {
        throw new Error(`Unknown store mutation: ${type}`);
      }
      return mutation(appStore, payload);
    },
  },
  dispatch: {
    value(type, payload) {
      const action = actionHandlers[type];
      if (!action) {
        throw new Error(`Unknown store action: ${type}`);
      }
      return action(createActionContext(appStore), payload);
    },
  },
});

export default appStore;
